/**
 * Temporary screen-scoped design tokens for **Sign Requests** (`/requests/sent`).
 *
 * Mirrors `Sign Requests.dc.html` 1:1 — every colour, size, radius, spacing and
 * type value the screen needs lives here so the components never inline a
 * literal. Once every screen is re-skinned these get promoted into `src/theme`
 * and this file is deleted (see UI_IMPLEMENTATION_RULES.md § Temporary Screen
 * Tokens).
 */

import { button, contentCard, formField, inkfree, notification, signer, white } from "theme/colors";

export const signRequestsTokens = {
  /**
   * Inner gutters of the content column. The column itself is centred by
   * `CenteredAuthenticatedLayout`, the same wrapper Templates uses.
   */
  page: {
    paddingTop: "32px",
    paddingX: "40px",
    paddingBottom: "48px",
  },

  /** "Sign Requests" title block + the two header actions. */
  header: {
    gap: "12px",
    marginBottom: "22px",
    titleColor: signer.ink,
    titleFontSize: "26px",
    titleFontWeight: 800,
    titleLetterSpacing: "-.02em",
    subtitleColor: signer.text.subtitle,
    subtitleFontSize: "13.5px",
    subtitleMarginTop: "5px",
    /** Stands in for "Showing 10 of 1326" while the first page loads. */
    countSkeletonWidth: 132,
  },

  /** Outlined "Create Folder" button. */
  secondaryButton: {
    bg: white,
    color: signer.text.secondary,
    border: button.secondaryBorderWarm,
    radius: "10px",
    padding: "12px 16px",
    fontSize: "13.5px",
    fontWeight: 700,
    gap: "8px",
    iconSize: 16,
    hoverBorder: signer.ink,
    hoverColor: signer.ink,
  },

  /** Filled "Create Request" split button. */
  primaryButton: {
    bg: inkfree.main,
    hoverBg: inkfree.dark,
    color: white,
    radius: "10px",
    padding: "12px 18px",
    fontSize: "13.5px",
    fontWeight: 700,
    gap: "9px",
    iconSize: 16,
    /**
     * Caret half of the split button. The header design draws a single button;
     * the app needs the request-type menu, so the caret reuses the width and
     * darker fill the design gives its sidebar split button.
     */
    caretWidth: 42,
    caretBg: inkfree.dark,
    caretHoverBg: inkfree.darkest,
    caretDivider: "rgba(255,255,255,.25)",
    caretIconSize: 14,
  },

  /** Home ▸ Folder ▸ … trail. */
  breadcrumb: {
    gap: "8px",
    // Same gap the search / filter row below it leaves — keep in step with
    // `toolbar.marginBottom` so the two blocks are spaced alike.
    marginBottom: "14px",
    fontSize: "14px",
    fontWeight: 600,
    iconSize: 16,
    activeColor: signer.ink,
    linkColor: signer.orange.main,
    separatorColor: signer.border.strong,
    separatorSize: 14,
    /** Longer folder names are clipped before the trail wraps. */
    maxLabelChars: 24,
  },

  /** Search field + date-range trigger. */
  toolbar: {
    gap: "14px",
    // Kept in step with `breadcrumb.marginBottom` — the trail sits the same
    // distance above this row as this row sits above the status tabs.
    marginBottom: "14px",
    bg: white,
    border: formField.borderWarm,
    radius: "11px",
    padding: "12px 16px",
    focusBorder: signer.orange.main,
    hoverBorder: "#D8D2C2",
    searchGap: "11px",
    searchIconSize: 18,
    searchIconColor: signer.text.faint,
    inputFontSize: "14px",
    inputColor: signer.ink,
    placeholderColor: signer.text.faint,
    dateGap: "9px",
    dateFontSize: "13.5px",
    dateFontWeight: 600,
    dateColor: signer.text.meta,
    dateIconSize: 16,
    dateCaretSize: 13,
  },

  /** Status pill tabs with their counts. */
  tabs: {
    gap: "8px",
    marginBottom: "16px",
    padding: "9px 15px",
    radius: "999px",
    fontSize: "13px",
    fontWeight: 700,
    labelGap: "8px",
    activeBg: signer.ink,
    activeBorder: signer.ink,
    activeColor: white,
    bg: white,
    border: formField.borderWarm,
    color: signer.text.meta,
    countMinWidth: 20,
    countPadding: "1px 6px",
    countRadius: "999px",
    countFontSize: "11px",
    countFontWeight: 700,
    countActiveBg: "rgba(255,255,255,.2)",
    countActiveColor: white,
    countBg: signer.border.divider,
    countColor: signer.text.subtitle,
  },

  /** White card wrapping every folder / request row. */
  list: {
    bg: contentCard.bg,
    border: contentCard.border,
    radius: "14px",
    shadow: contentCard.shadow,
  },

  /**
   * Shared row grid: name | signers | status | kebab. Folder rows leave the
   * middle two columns empty and put a chevron in the last one.
   */
  row: {
    gridTemplateColumns: "minmax(0,1fr) 156px 236px 44px",
    gap: "18px",
    folderPadding: "15px 24px",
    requestPadding: "16px 24px",
    divider: signer.border.divider,
    hoverBg: signer.surface.hover,
    /** Icon tile + text block. */
    leadGap: "13px",
    iconTileSize: 40,
    iconTileRadius: "9px",
    folderIconSize: 19,
    requestIconSize: 18,
    folderIconBg: signer.orange.hover,
    folderIconStroke: signer.orange.main,
    titleFontSize: "14px",
    titleFontWeight: 700,
    titleColor: signer.ink,
    /** Draft / declined / expired / voided rows dim their title. */
    titleMutedColor: "#6F6957", // WCAG 1.4.3: #8A836F was 3.77:1 on white
    subFontSize: "12px",
    subColor: signer.text.faint,
    folderSubMarginTop: "2px",
    requestSubMarginTop: "3px",
    chevronColor: signer.border.strong,
    chevronSize: 16,
  },

  /** Overlapping signer initials. */
  avatars: {
    paddingLeft: "8px",
    size: 30,
    overlap: "-8px",
    ring: white,
    ringWidth: "2px",
    fontSize: "10.5px",
    fontWeight: 700,
    color: white,
    moreBg: signer.border.light,
    moreColor: signer.text.meta,
    moreFontSize: "11px",
    /** Avatars shown before collapsing into a "+N" bubble. */
    maxVisible: 3,
    emptyLabel: "Not assigned",
    emptyFontSize: "12.5px",
    emptyColor: signer.text.faint, // content, not a disabled control: disabledColor is 2.06:1
  },

  /** Status chip + signer progress bar. */
  status: {
    chipGap: "7px",
    chipPadding: "5px 11px",
    chipRadius: "999px",
    dotSize: 7,
    labelFontSize: "12px",
    labelFontWeight: 700,
    progressMarginTop: "8px",
    progressGap: "10px",
    barHeight: 5,
    barRadius: "3px",
    barTrack: signer.border.light,
    noteFontSize: "11px",
    noteFontWeight: 600,
    noteColor: signer.text.faint,
    /** Shown instead of "n/m" when a draft was never sent. */
    draftNote: "Not sent",
    emptyNote: "—",
  },

  /** Row overflow menu trigger. */
  kebab: {
    size: 32,
    radius: "8px",
    color: signer.text.subtitle,
    iconSize: 18,
    hoverBg: signer.border.divider,
    hoverColor: signer.ink,
  },

  /** In-card "nothing matches" block. */
  empty: {
    padding: "56px 24px",
    titleFontSize: "14px",
    titleFontWeight: 600,
    titleColor: signer.text.meta,
    subFontSize: "12.5px",
    subMarginTop: "4px",
    subColor: signer.text.faint,
  },

  /** Full-card placeholder for a brand-new account. */
  freshState: {
    border: contentCard.border,
    radius: "16px",
    shadow: contentCard.shadow,
    padding: "110px 24px",
    iconSize: 72,
    iconBg: signer.orange.tint,
    iconStroke: signer.orange.main,
    iconGlyphSize: 30,
    iconMarginBottom: "20px",
    titleFontSize: "18px",
    titleFontWeight: 800,
    titleColor: signer.ink,
    bodyFontSize: "13.5px",
    bodyColor: signer.text.subtitle,
    bodyMaxWidth: 440,
    bodyLineHeight: 1.65,
    bodyMarginTop: "8px",
    actionsMarginTop: "26px",
    actionsGap: "12px",
    buttonHeight: 46,
  },
} as const;

/**
 * Per-status palette: chip fill / dot / label ink and the matching document
 * icon tile. Keyed by the app's `SignRequest.status` values; `getStatusKey`
 * maps anything unrecognised onto the neutral "voided" look.
 */
export const signRequestsStatusTokens = {
  draft: {
    label: "Draft",
    bg: signer.surface.pill,
    dot: signer.text.faint,
    color: signer.text.meta,
    iconBg: signer.surface.pill,
    iconStroke: signer.text.faint,
    muted: true,
  },
  pending: {
    label: "Pending",
    bg: notification.warning.bg,
    dot: "#E0A82E",
    color: notification.warning.icon,
    iconBg: signer.orange.tint,
    iconStroke: signer.orange.main,
    muted: false,
  },
  // Will send later — the filtered list's own status, drawn in the design's
  // purple so it does not read as a draft.
  scheduled: {
    label: "Scheduled",
    bg: signer.purple.tint,
    dot: signer.purple.main,
    color: signer.purple.dark,
    iconBg: signer.purple.tint,
    iconStroke: signer.purple.main,
    muted: false,
  },
  // Waiting on an approver rather than a signer.
  approval: {
    label: "Awaiting approval",
    bg: "#E5F5F4",
    dot: "#159B94",
    color: "#0E6E69",
    iconBg: "#E5F5F4",
    iconStroke: "#159B94",
    muted: false,
  },
  progress: {
    label: "In progress",
    bg: "#EAF2FC",
    dot: "#2A7DE1",
    color: "#1B5DAC",
    iconBg: "#EAF2FC",
    iconStroke: "#2A7DE1",
    muted: false,
  },
  completed: {
    label: "Completed",
    bg: notification.success.bg,
    dot: notification.success.icon,
    color: notification.success.title,
    iconBg: notification.success.bg,
    iconStroke: notification.success.icon,
    muted: false,
  },
  declined: {
    label: "Declined",
    bg: notification.error.bg,
    dot: notification.error.icon,
    color: notification.error.title,
    iconBg: notification.error.bg,
    iconStroke: notification.error.icon,
    muted: true,
  },
  // Reviewer rejection — same red family as a signer decline, its own label so
  // the Rejected tab doesn't read as "Declined".
  rejected: {
    label: "Rejected",
    bg: notification.error.bg,
    dot: notification.error.icon,
    color: notification.error.title,
    iconBg: notification.error.bg,
    iconStroke: notification.error.icon,
    muted: true,
  },
  expired: {
    label: "Expired",
    bg: "#F3F1EC",
    dot: "#A69F8C",
    color: "#6F6957", // WCAG 1.4.3: #8A836F was 3.34:1 on its chip fill
    iconBg: "#F3F1EC",
    iconStroke: "#A69F8C",
    muted: true,
  },
  voided: {
    label: "Voided",
    bg: signer.border.light,
    dot: signer.text.subtitle,
    color: signer.text.meta,
    iconBg: signer.border.light,
    iconStroke: signer.text.subtitle,
    muted: true,
  },
} as const;

export type SignRequestsStatusKey = keyof typeof signRequestsStatusTokens;

/**
 * Raw media query for the narrow phone column of
 * `Sign Requests - Mobile & Tablet.dc.html`.
 *
 * The reference draws 375 and 390 as two different screens, not one fluid
 * layout: the narrow column drops the "Create Request" split button for a
 * floating pill and collapses the date chip to an icon-only square, while the
 * standard column keeps both in full. MUI has no breakpoint between the two, so
 * the split is a raw query applied *after* the `xs` values so it wins.
 */
export const PHONE_SM_QUERY = "@media (max-width:389.98px)";

/**
 * Phone (`xs`) and tablet (`sm`) values, traced from
 * `Sign Requests - Mobile & Tablet.dc.html`.
 *
 * Only values that actually differ from the desktop tokens above live here;
 * anything omitted is inherited, so the desktop screen is untouched. `sm` /
 * `std` inside `phone` are the reference's 375 and 390 columns.
 */
export const signRequestsResponsiveTokens = {
  phone: {
    /** Content column: 14px gutters at 375, 16px at 390. */
    paddingX: { sm: "14px", std: "16px" },
    paddingTop: { sm: "16px", std: "18px" },
    paddingBottom: { sm: "32px", std: "34px" },
    /** One column gap replaces the desktop screen's per-block bottom margins. */
    gap: { sm: "14px", std: "15px" },

    /** The `<h1>` moves into the app bar; only the strapline stays on the page. */
    subtitleFontSize: { sm: "12.5px", std: "13px" },
    subtitleLineHeight: 1.45,

    /**
     * Full-width "Create Request" split button, drawn under the strapline from
     * 390 up. The 375 column replaces it with the floating pill below.
     */
    createButton: {
      padding: "13px 14px",
      radius: "11px",
      fontSize: "14px",
      gap: "9px",
      iconSize: 16,
      caretWidth: 44,
      caretIconSize: 14,
    },

    /** Search field; the date control beside it collapses (see `dateChip`). */
    search: {
      padding: "11px 13px",
      gap: "9px",
      iconSize: 16,
      inputFontSize: "13px",
      /** Neither phone column has room for the full sentence. */
      placeholderShort: "Search requests",
    },

    /**
     * 390 keeps a compact "All time" chip (no caret); 375 shrinks it to a
     * 46px icon-only square carrying the reference's filter-lines glyph.
     */
    dateChip: {
      padding: "0 13px",
      gap: "8px",
      fontSize: "12.5px",
      iconSize: 15,
      squareWidth: 46,
      squareIconSize: 17,
    },

    /** Chips run as one horizontally-scrolling line instead of wrapping. */
    tabs: {
      gap: "8px",
      padding: { sm: "8px 13px", std: "9px 14px" },
      fontSize: "12.5px",
    },

    /** Every folder / request becomes its own card, stacked with a gap. */
    list: { gap: "10px" },

    folderRow: {
      padding: "13px 14px",
      radius: "14px",
      gap: "12px",
      iconTileSize: 40,
      iconTileRadius: "11px",
      iconSize: 19,
      titleFontSize: { sm: "13.5px", std: "14px" },
      subFontSize: "11.5px",
    },

    requestRow: {
      padding: "14px",
      radius: "14px",
      /** Between the card's three lines. */
      blockGap: "11px",
      /** Icon tile to title block on the first line. */
      headGap: { sm: "11px", std: "12px" },
      iconTileSize: { sm: 38, std: 40 },
      iconTileRadius: "10px",
      iconSize: { sm: 17, std: 18 },
      titleFontSize: { sm: "13px", std: "13.5px" },
      subFontSize: { sm: "11px", std: "11.5px" },
      /** The kebab is pulled into the card's top-right corner. */
      kebabSize: 30,
      kebabIconSize: 17,
      kebabMargin: "-4px -4px 0 0",
    },

    avatars: {
      size: { sm: 26, std: 28 },
      overlap: "-7px",
      fontSize: { sm: "9.5px", std: "10px" },
      emptyFontSize: { sm: "11.5px", std: "12px" },
    },

    status: {
      chipPadding: { sm: "5px 10px", std: "5px 11px" },
      chipGap: "6px",
      dotSize: 6,
      labelFontSize: { sm: "11px", std: "11.5px" },
      progressGap: "9px",
      noteFontSize: { sm: "10.5px", std: "11px" },
    },

    empty: {
      radius: "14px",
      padding: { sm: "44px 20px", std: "48px 20px" },
      titleFontSize: { sm: "13.5px", std: "14px" },
      titleFontWeight: 700,
      subFontSize: { sm: "12px", std: "12.5px" },
    },

    freshState: {
      radius: "16px",
      padding: { sm: "44px 20px", std: "52px 22px" },
      iconSize: { sm: 62, std: 66 },
      glyphSize: { sm: 26, std: 28 },
      iconMarginBottom: { sm: "16px", std: "17px" },
      titleFontSize: { sm: "16px", std: "17px" },
      bodyFontSize: { sm: "12.5px", std: "13px" },
      bodyLineHeight: 1.6,
      actionsMarginTop: { sm: "22px", std: "24px" },
    },
  },

  tablet: {
    paddingX: "26px",
    paddingTop: "26px",
    paddingBottom: "40px",
    gap: "18px",

    titleFontSize: "24px",
    subtitleFontSize: "13px",
    /** Between the folder square and the split button. */
    actionsGap: "10px",

    /** "Create Folder" loses its label and becomes a 44px square. */
    secondaryButton: { size: 44, radius: "10px", iconSize: 17 },

    breadcrumb: { fontSize: "13.5px", iconSize: 16 },

    toolbar: {
      gap: "12px",
      padding: "12px 15px",
      searchGap: "11px",
      searchIconSize: 17,
      inputFontSize: "13.5px",
      dateGap: "9px",
      dateFontSize: "13px",
      dateIconSize: 16,
    },

    row: {
      /** Narrower signer / status tracks than the desktop grid. */
      gridTemplateColumns: "minmax(0,1fr) 130px 210px 40px",
      gap: "16px",
      folderPadding: "15px 20px",
      requestPadding: "15px 20px",
      folderLeadGap: "14px",
      requestLeadGap: "12px",
      folderTitleFontSize: "14px",
      folderSubFontSize: "12px",
      requestTitleFontSize: "13.5px",
      requestSubFontSize: "11.5px",
    },

    avatars: { paddingLeft: "6px" },

    freshState: { padding: "96px 24px" },
  },
} as const;

export default signRequestsTokens;
